import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Link as LinkIcon, Copy, Trash, Eye, Check, Clock, SpinnerGap,
  CaretDown, CaretUp, Warning, PaperPlaneTilt,
} from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function ApprovalsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [statusData, setStatusData] = useState({});

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const resp = await api.get("/approvals");
        setLinks(resp.data.approval_links || []);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetchLinks();
  }, [api]);

  const toggleExpand = async (linkId) => {
    if (expandedId === linkId) { setExpandedId(null); return; }
    setExpandedId(linkId);
    if (!statusData[linkId]) {
      try {
        const resp = await api.get(`/approvals/${linkId}/status`);
        setStatusData(prev => ({ ...prev, [linkId]: resp.data }));
      } catch { /* silent */ }
    }
  };

  const toggleActive = async (linkId, currentActive) => {
    try {
      await api.patch(`/approvals/${linkId}`, { is_active: !currentActive });
      setLinks(prev => prev.map(l => l.link_id === linkId ? { ...l, is_active: !currentActive } : l));
      toast.success(!currentActive ? "Link activated" : "Link deactivated");
    } catch { toast.error("Failed to update link"); }
  };

  const deleteLink = async (linkId) => {
    try {
      await api.delete(`/approvals/${linkId}`);
      setLinks(prev => prev.filter(l => l.link_id !== linkId));
      toast.success("Link deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/review/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied!");
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']" data-testid="approvals-page-title">Client Approvals</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">Track content review links shared with clients</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#FF4400]" /></div>
        ) : links.length === 0 ? (
          <div className="text-center py-20" data-testid="approvals-empty-state">
            <PaperPlaneTilt size={48} className="mx-auto text-[var(--text-muted)] opacity-30 mb-4" weight="duotone" />
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No approval links yet</h2>
            <p className="text-sm text-[var(--text-muted)] mb-6 max-w-md mx-auto">
              Share content for client review from any history item. Your clients can approve or request changes without logging in.
            </p>
            <Button onClick={() => navigate("/history")} className="bg-[#FF4400] hover:bg-[#FF6633] text-white">Go to History</Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="approvals-list">
            {links.map((link, idx) => {
              const approved = (link.approvals || []).filter(a => a.decision === "approved").length;
              const total = (link.history_item_ids || []).length;
              const isExpired = link.expires_at && new Date(link.expires_at) < new Date();
              const sd = statusData[link.link_id];

              return (
                <motion.div key={link.link_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg overflow-hidden" data-testid={`approval-row-${idx}`}>
                  <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => toggleExpand(link.link_id)}>
                    <div className="w-9 h-9 rounded-lg bg-[#FF4400]/10 flex items-center justify-center shrink-0">
                      <LinkIcon size={18} className="text-[#FF4400]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[var(--text-primary)] truncate" data-testid={`approval-title-${idx}`}>{link.title}</span>
                        {!link.is_active && <span className="px-1.5 py-0.5 text-[9px] rounded bg-gray-500/10 text-gray-400">Inactive</span>}
                        {isExpired && <span className="px-1.5 py-0.5 text-[9px] rounded bg-red-500/10 text-red-400">Expired</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-[var(--text-muted)]">
                        <span>{total} item{total !== 1 ? "s" : ""}</span>
                        <span className="text-[var(--border)]">|</span>
                        <span className="flex items-center gap-1"><Eye size={10} /> {link.views} views</span>
                        <span className="text-[var(--border)]">|</span>
                        <span className={approved === total && total > 0 ? "text-emerald-400" : ""}>{approved} of {total} approved</span>
                        <span className="text-[var(--border)]">|</span>
                        <span>{formatDate(link.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                      <button onClick={() => copyLink(link.token)} className="p-1.5 text-[var(--text-muted)] hover:text-[#FF4400] transition-colors" title="Copy link" data-testid={`copy-link-${idx}`}>
                        <Copy size={14} />
                      </button>
                      <Switch checked={link.is_active} onCheckedChange={() => toggleActive(link.link_id, link.is_active)} data-testid={`toggle-active-${idx}`} />
                      <button onClick={() => deleteLink(link.link_id)} className="p-1.5 text-[var(--text-muted)] hover:text-red-400 transition-colors" data-testid={`delete-link-${idx}`}>
                        <Trash size={14} />
                      </button>
                      {expandedId === link.link_id ? <CaretUp size={14} className="text-[var(--text-muted)]" /> : <CaretDown size={14} className="text-[var(--text-muted)]" />}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {expandedId === link.link_id && (
                    <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} className="border-t border-[var(--border)] px-4 py-3 space-y-2 bg-[var(--bg-base)]" data-testid={`approval-detail-${idx}`}>
                      {sd ? sd.items.map((item) => {
                        const ca = item.client_approval || {};
                        return (
                          <div key={item.history_id || item.title} className="flex items-center justify-between py-2 border-b border-[var(--border)] last:border-0">
                            <span className="text-sm text-[var(--text-secondary)] truncate flex-1">{item.title}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              {ca.status === "approved" && <span className="text-xs text-emerald-400 flex items-center gap-1"><Check size={12} weight="bold" /> Approved</span>}
                              {ca.status === "changes_requested" && <span className="text-xs text-amber-400 flex items-center gap-1"><Warning size={12} /> Changes requested</span>}
                              {ca.status === "pending" && <span className="text-xs text-gray-400 flex items-center gap-1"><Clock size={12} /> Pending</span>}
                              {(!ca.status || ca.status === "not_sent") && <span className="text-xs text-gray-400">—</span>}
                            </div>
                          </div>
                        );
                      }) : <div className="flex justify-center py-3"><SpinnerGap size={16} className="animate-spin text-[var(--text-muted)]" /></div>}
                      {sd && sd.items.some(i => (i.client_approval || {}).client_note) && (
                        <div className="pt-2">
                          {sd.items.filter(i => (i.client_approval || {}).client_note).map((item) => (
                            <div key={`note-${item.history_id || item.title}`} className="text-xs text-[var(--text-muted)] italic py-1">
                              <span className="font-medium text-[var(--text-secondary)]">{item.title}:</span> "{item.client_approval.client_note}"
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
